import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, ChevronRight, Shield, Users, Calendar, DollarSign, Star, Repeat } from 'lucide-react';

const JourniLogo = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 200 200" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M120 30 L120 120 Q120 150 90 150 L70 150" fill="#10b981" />
    <path d="M140 30 L160 30 Q170 30 170 40 L170 80" fill="#10b981" />
    <path d="M50 130 L70 150 L70 170 Q70 180 60 180 L40 180 Q30 180 30 170 L30 130 Z" fill="#10b981" />
    <circle cx="90" cy="130" r="20" fill="white" />
    <circle cx="90" cy="130" r="12" fill="#10b981" />
  </svg>
);

export default function JourniLanding() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <JourniLogo />
              <span className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Journi
              </span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 hover:text-emerald-600 transition">Features</a>
              <a href="#how-it-works" className="text-gray-600 hover:text-emerald-600 transition">How It Works</a>
              <a href="#why-journi" className="text-gray-600 hover:text-emerald-600 transition">Why Journi</a>
              <button 
                onClick={() => navigate('/signup')}
                className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:shadow-lg transition">
                Get Started
              </button>
            </div>

            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block text-gray-600 hover:text-emerald-600">Features</a>
              <a href="#how-it-works" className="block text-gray-600 hover:text-emerald-600">How It Works</a>
              <a href="#why-journi" className="block text-gray-600 hover:text-emerald-600">Why Journi</a>
              <button 
                onClick={() => navigate('/signup')}
                className="w-full px-6 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg">
                Get Started
              </button>
            </div>
          </div>
        )}
      </nav>

      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-4 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
                Pakistan's First Comprehensive Carpooling Platform
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Daily Carpooling
                <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Made Safe, Simple & Sustainable
                </span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                The smart way to commute. Save money, reduce traffic, and fight climate change—one shared ride at a time. Built for Pakistan, designed for safety, trusted by communities.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => navigate('/signup')}
                  className="px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-semibold hover:shadow-xl transition flex items-center justify-center gap-2">
                  Get Started
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button className="px-8 py-4 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:border-emerald-500 hover:text-emerald-600 transition">
                  Learn More
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  <span>4.8/5 Rating</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  <span>2,000+ Active Users</span>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-100 to-teal-100 rounded-3xl p-8 shadow-2xl">
                <div className="bg-gradient-to-br from-emerald-200 to-teal-200 rounded-2xl h-64 flex items-center justify-center">
                  <JourniLogo className="w-32 h-32" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Shield className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Safe & Verified</div>
                      <div className="text-sm text-gray-500">CNIC verified drivers</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="why-journi" className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Pakistan Needs Journi Now
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <p className="text-lg text-gray-700 mb-6">
                Pakistan contributes less than 1% of global carbon emissions, yet we suffer devastating floods, heatwaves, and climate disasters. Over 33 million people were affected by the 2022 floods alone—that's more than the population of Texas.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                We didn't create this crisis, but we're paying the price. Every car on the road adds to the problem. Every empty seat is a missed opportunity to make a difference.
              </p>
              <p className="text-lg font-semibold text-emerald-700">
                Journi is our response. By sharing rides, we reduce emissions, ease traffic congestion, save money, and build stronger communities. One carpool at a time, we're taking control of our future.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Makes Journi Different?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need for safe, reliable, and affordable daily carpooling
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Safety Through Verification', items: ['CNIC Verification for every driver', 'Reliability Scores track no-shows', 'Female-Friendly Filters', 'Community Ratings'] },
              { icon: Users, title: 'Made for Parents & Teachers', items: ['School Run Coordination', 'Child Safety Features', 'Schedule Management', 'Group Pooling'] },
              { icon: Repeat, title: 'Recurring Rides', items: ['Set schedule once', 'Skip rides with one tap', 'No daily coordination', 'Perfect for commutes'] },
              { icon: DollarSign, title: 'Fair Pay Calculator', items: ['Transparent pricing', 'Based on fuel costs', 'No arguments', 'Fair cost-sharing'] },
              { icon: Star, title: 'Community Building', items: ['Connect with neighbors', 'Long-term relationships', 'Rate & review', '3-strike accountability'] },
              { icon: Calendar, title: 'Environmental Impact', items: ['Track carbon savings', 'See CO₂ prevented', 'Climate action movement', 'Make a difference'] }
            ].map((feature, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition">
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <ul className="text-gray-600 space-y-2">
                  {feature.items.map((item, j) => <li key={j}>• {item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Get started in 3 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: 1, title: 'Create Profile', desc: 'Sign up with your phone number, add your preferences, and verify your CNIC for trust.' },
              { num: 2, title: 'Find or Post Rides', desc: 'Search for rides on your route or post your own. Set up recurring schedules for regular commutes.' },
              { num: 3, title: 'Carpool Safely', desc: 'Connect with verified users, share rides, and build your trusted carpooling community.' }
            ].map((step) => (
              <div key={step.num} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-emerald-600 to-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Carpooling?
          </h2>
          <p className="text-xl text-emerald-50 mb-8">
            Join thousands of commuters saving money and making their daily travel safer and easier.
          </p>
          <button 
            onClick={() => navigate('/signup')}
            className="px-8 py-4 bg-white text-emerald-600 rounded-xl font-semibold hover:shadow-2xl transition text-lg">
            Download Journi Now
          </button>
        </div>
      </section>

      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <JourniLogo className="w-8 h-8" />
                <span className="text-xl font-bold text-white">Journi</span>
              </div>
              <p className="text-sm">
                Making daily carpooling safe, simple, and reliable.
              </p>
            </div>
            
            {[
              { title: 'Product', links: ['Features', 'How It Works', 'Why Journi'] },
              { title: 'Company', links: ['About Us', 'Contact', 'Careers'] },
              { title: 'Legal', links: ['Privacy Policy', 'Terms of Service'] }
            ].map((col, i) => (
              <div key={i}>
                <h4 className="font-semibold text-white mb-4">{col.title}</h4>
                <ul className="space-y-2 text-sm">
                  {col.links.map((link, j) => (
                    <li key={j}><a href="#" className="hover:text-emerald-400 transition">{link}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-sm text-center">
            © 2025 Journi. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}